<template>
  <TransitionGroup name="appear">
    <!-- Host not found -->
    <NotFound
      v-if="hostNotFound || !inviteCode"
      data-test="host-not-found"
    >
      <span v-if="!inviteCode">
        The invite code is missing. Please check the URL or contact your company administrator for assistance.
      </span>
    </NotFound>

    <!-- Loader-->
    <div
      v-else-if="
        processing ||
        !donorStore.hosts_id ||
        (authStore.authenticated && !donorStore.isDataLoaded) ||
        companyAdminStore.companyLoading
      "
      class="flex grow absolute inset-x-0 inset-y-0"
    >
      <div class="flex grow items-center justify-center w-full">
        <div class="flex flex-row items-center justify-center h-full">
          <div class="loader"></div>
        </div>
      </div>
    </div>

    <!-- Success -->
    <div
      v-else-if="success"
      class="flex grow"
    >
      <div class="flex items-center justify-center min-h-screen bg-gray-100 w-full">
        <Card
          class="flex flex-col max-w-3xl mx-4 p-0 lg:px-6"
          ref="mainContent"
        >
          <template #content>
            <div
              class="flex flex-col items-center gap-8"
              v-if="companyAdminStore.company.status == companyGivingStatus.PENDING_APPROVAL"
            >
              <div><i class="fa fa-check-circle text-5xl text-primary"></i></div>
              <h2 class="text-4xl font-bold">You have successfully joined</h2>
              <p class="text-lg max-w-2xl">
                Your Company Giving application is still pending approval. Once approved, you can log in again and
                complete your organization's Company Giving profile.
              </p>
              <Button
                @click="authStore.logout()"
                size="large"
                variant="primary"
                class="px-12"
              >
                Log Out
              </Button>
            </div>
            <div
              class="flex flex-col items-center gap-8"
              v-if="companyAdminStore.company.status == companyGivingStatus.PENDING_PROFILE"
            >
              <div><i class="fa fa-check-circle text-5xl text-primary"></i></div>
              <h2 class="text-4xl font-bold">You have successfully joined</h2>
              <p class="text-lg max-w-2xl">
                You can now complete your organization's Company Giving profile. Click the button below to get started.
              </p>
              <Button
                @click="router.push('/company/profile')"
                size="large"
                variant="primary"
                class="px-12"
              >
                Get Started
              </Button>
            </div>
            <div
              class="flex flex-col items-center gap-8"
              v-if="companyAdminStore.company.status == companyGivingStatus.APPROVED"
            >
              <div><i class="fa fa-check-circle text-5xl text-primary"></i></div>
              <h2 class="text-4xl font-bold">You have successfully joined</h2>
              <p class="text-lg max-w-2xl">
                You can now manage your organization's Company Giving profile. Click the button below to get started.
              </p>
              <Button
                @click="router.push('/company/overview')"
                size="large"
                variant="primary"
                class="px-12"
              >
                Get Started
              </Button>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <!-- Main container -->
    <StepsTakeover v-else>
      <!-- Top bar -->
      <template #top>
        <div class="host-image p-1 pl-4 h-[72px] flex justify-center">
          <img
            class="h-full object-contain"
            :src="donorStore.hostLogo"
            :alt="donorStore.hostName"
          />
        </div>
        <div class="mr-6">
          <Button
            class="hidden lg:block !text-sm"
            variant="link"
            as="a"
            :href="donorStore.hostUrl"
          >
            Return to Gives Day Home
          </Button>
        </div>
      </template>

      <!-- Bottom bar -->
      <template #bottom>
        <div class="flex flex-row items-center"></div>
        <div class="flex flex-row items-center gap-4">
          <TransitionGroup name="fade">
            <Button
              key="b-2"
              :disabled="!activeFormValid"
              :loading="loading"
              :label="nextLabel"
              @click="next()"
              class="px-12"
            />
          </TransitionGroup>
        </div>
      </template>

      <!-- Content container -->
      <template #main>
        <div v-if="authStore.authenticated">
          <h2 class="text-2xl mb-6">Donor Account</h2>
          <p class="text-base max-w-xs sm:max-w-[430px] mb-6">
            A donor account is required in order to join a Company Giving profile. Review your information below and
            click "Next" to continue.
          </p>
          <AuthStatus />
        </div>
        <div v-else>
          <h2 class="text-2xl mb-6">Donor Account Login</h2>
          <p class="text-base max-w-xs sm:max-w-[430px] mb-6">
            A donor account is required in order to join a Company Giving profile. If you have a donor account already,
            please log in below. If you do not have a donor account, creating one is easy!
          </p>
          <div class="flex flex-col flex-wrap gap-4 mb-6 ml-2">
            <div class="flex items-center gap-2">
              <RadioButton
                v-model="existingAccount"
                :value="true"
                inputId="existingAccount-1"
              />
              <label for="existingAccount-1">I have a donor account</label>
            </div>
            <div class="flex items-center gap-2">
              <RadioButton
                v-model="existingAccount"
                :value="false"
                inputId="existingAccount-0"
              />
              <label for="existingAccount-0">I need to create a donor account</label>
            </div>
          </div>
          <div class="min-h-[350px]">
            <TransitionGroup name="fade">
              <LoginForm
                :key="1"
                v-show="existingAccount === true"
                class="max-w-xs sm:max-w-[430px]"
                @submit="next"
              />
              <SignupForm
                :key="0"
                v-show="existingAccount === false"
                class="max-w-xs sm:max-w-[430px]"
                @submit="next"
              />
            </TransitionGroup>
          </div>
        </div>
      </template>
    </StepsTakeover>
  </TransitionGroup>
</template>

<script setup>
import { ref, onBeforeMount, computed } from 'vue';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useToastStore } from '@/stores/ToastStore.js';
import { useRouter } from 'vue-router';
import { companyGivingStatus } from '@/helpers/enum.js';
import AuthStatus from '@/components/auth/AuthStatus.vue';
import LoginForm from '@/components/auth/LoginForm.vue';
import SignupForm from '@/components/auth/SignupForm.vue';
import StepsTakeover from '@/components/StepsTakeover.vue';
import NotFound from '@/pages/NotFound.vue';

const authStore = useAuthStore();
const companyAdminStore = useCompanyAdminStore();
const donorStore = useDonorStore();
const existingAccount = ref(null);
const hostNotFound = ref(false);
const loading = ref(false);
const processing = ref(false);
const success = ref(false);

// Invite code from URL
const router = useRouter();
const inviteCode = router.currentRoute.value.query.code;

onBeforeMount(async () => {
  try {
    await donorStore.fetchHostFromPortalDomain();
    await authStore.checkLocalStorage();
  } catch (error) {
    hostNotFound.value = true;
  }
});

const nextLabel = computed(() => {
  if (authStore.authenticated) {
    return 'Next';
  }

  if (existingAccount.value) {
    return 'Log In';
  }

  return 'Sign Up';
});

const activeFormValid = computed(() => {
  if (authStore.authenticated) {
    return true;
  }

  if (existingAccount.value) {
    return authStore.loginForm?.valid;
  }

  return authStore.signupForm?.valid;
});

const next = async () => {
  if (loading.value || processing.value) return;
  loading.value = true;
  processing.value = true;

  try {
    if (!authStore.authenticated) {
      // Check email address and validation code prior to proceeding
      if (!existingAccount.value) {
        await companyAdminStore.checkInvitation(inviteCode, authStore.signupForm.states.email.value);
      } else {
        await companyAdminStore.checkInvitation(inviteCode, authStore.loginForm.states.email.value);
      }

      // Log in or sign up
      if (existingAccount.value) {
        await authStore.donorLogin();
      } else {
        await authStore.donorSignup();
      }
    }

    // Accept invitation
    await companyAdminStore.acceptInvitation(inviteCode);

    // Switch to the company giving account
    const activePassword = existingAccount.value
      ? authStore.loginForm.states.password.value
      : authStore.signupForm.states.password.value;
    await authStore.switchAccount(donorStore.donorInfo.email, activePassword);
    await companyAdminStore.getCompany();

    // Show success message
    success.value = true;
  } catch (e) {
    console.error(e);
    // Report error as a Toast notification
    useToastStore().add({
      severity: 'error',
      summary: 'Error',
      detail: e.response?.data?.message ?? e.message,
      life: 3000,
    });
  } finally {
    loading.value = false;
    processing.value = false;
  }
};
</script>
